import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, CircularProgress } from "@mui/material";

export const useJivoChat = () => {
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  const openJivoChat = useCallback(async () => {
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setLoading(true);

    try {
      if (typeof window.openJivoChat !== "function") {
        throw new Error("Jivo loader is unavailable");
      }

      await window.openJivoChat();
    } catch (error) {
      console.error("Unable to open Jivo chat", error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  return { loading, openJivoChat };
}

export const JivoChatButton = ({ children, disabled, style, ...props }) => {
  const { loading, openJivoChat } = useJivoChat();

  return (
    <Button
      {...props}
      aria-busy={loading}
      disabled={disabled || loading}
      onClick={openJivoChat}
      style={{ position: "relative", ...style }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", visibility: loading ? "hidden" : "visible" }}>
        {children}
      </span>
      {loading && <CircularProgress color="inherit" size={20} style={{
        left: "50%",
        marginLeft: -10,
        marginTop: -10,
        position: "absolute",
        top: "50%",
      }} />}
    </Button>
  );
}

export const JivoChatTrigger = ({ children, component: Component = "span", hideWhenOpen = false, style, ...props }) => {
  const { loading, openJivoChat } = useJivoChat();
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    if (!hideWhenOpen) {
      return undefined;
    }

    const handleOpen = () => setChatOpen(true);
    const handleClose = () => setChatOpen(false);

    document.addEventListener("jivo:open", handleOpen);
    document.addEventListener("jivo:close", handleClose);

    return () => {
      document.removeEventListener("jivo:open", handleOpen);
      document.removeEventListener("jivo:close", handleClose);
    };
  }, [hideWhenOpen]);

  if (hideWhenOpen && chatOpen) {
    return null;
  }

  return (
    <Component
      {...props}
      aria-busy={loading}
      aria-disabled={loading}
      onClick={openJivoChat}
      style={{ position: "relative", ...style }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", visibility: loading ? "hidden" : "visible" }}>
        {children}
      </span>
      {loading && <CircularProgress color="inherit" size={20} style={{
        left: "50%",
        marginLeft: -10,
        marginTop: -10,
        position: "absolute",
        top: "50%",
      }} />}
    </Component>
  );
}
